/**
 * advance-cloak.ts — per-tick cloak energy drain + auto-decloak.
 *
 * Runs inside advanceTick *after* advanceShipStatus (so EMP disablement is
 * fresh) and *before* advanceShipMovement, so a just-decloaked ship's
 * visibility is already correct when the downstream detection pass rolls.
 *
 * Behaviour per cloaked ship:
 *   - EMP-disabled (disabledTicks > 0): auto-decloak. Cloak electronics drop
 *     out along with weapons and engines (MECHANICS §2.2).
 *   - Mid-jump (systemId === null): auto-decloak. Cloak has no meaning in
 *     hyperspace.
 *   - currentEnergy < cloakEnergyDrainPerTick: auto-decloak, clamp energy
 *     to 0. The drain would push below zero so the cloak can't sustain.
 *   - Otherwise: deduct cloakEnergyDrainPerTick from currentEnergy.
 *
 * What this file does NOT do:
 *   - Firing-breaks-cloak. That hook belongs to the future combat pass, which
 *     will set `ship.cloaked = false` at the moment of firing (gated on
 *     `empire.researchedTechs.has(ADVANCED_CLOAKING_TECH_ID)` — RACE-CC's
 *     "fire while cloaked" privilege). Keeping it in the firing code means
 *     detection sees the uncloaked ship in the same tick as the shot, with
 *     no 1-tick latency. Until combat lands, no ship ever fires and this
 *     hook is a documented TODO.
 *   - Energy regen. Handled wherever ship energy recovery eventually lives;
 *     cloak-drain only takes, it never gives.
 */

import {
  DEFAULT_DETECTION_BALANCE,
  type DetectionBalance,
} from './balance-detection';
import type { GameState } from '../state/types/index';

/** Deduct cloak upkeep energy, auto-decloak on drain/EMP/jump. */
export function advanceCloakEnergyDrain(
  state: GameState,
  balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): void {
  const drain = balance.cloakEnergyDrainPerTick;
  for (const ship of state.ships.values()) {
    if (!ship.cloaked) continue;

    if (ship.disabledTicks > 0) {
      ship.cloaked = false;
      continue;
    }
    if (ship.systemId === null) {
      ship.cloaked = false;
      continue;
    }
    if (ship.currentEnergy < drain) {
      ship.cloaked = false;
      ship.currentEnergy = 0;
      continue;
    }
    ship.currentEnergy -= drain;
  }
}
